// Sidebar del rol Coordinador (política 4 de coherencia, Lote 1 — 2026-09-07).
//
//   - Grupos PLEGABLES: Análisis abierto por defecto (es el trabajo diario);
//     Operación, Facturación y Configuración plegados, con conteo. El grupo
//     que contiene la ruta activa se abre solo. Sin scroll en 1366×768.
//   - Auditoría fuera de Configuración: es consulta, no configuración.
//   - Un icono = un significado (antes gauge/factory/alert/users/download
//     se repetían con sentidos distintos).
//
// El DIRECTIVO también entra a estas pantallas (solo lectura, scope
// todas-las-plantas — drill-down 2026-06-11): para él se ocultan los grupos
// de ACCIÓN (programación/facturación/configuración) y queda Dashboard + Análisis.

import { Sidebar, SidebarGroup, SidebarItem } from './SidebarNav';
import { useConectividadLineas } from '../../lib/api/ingesta';
import { useMe } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { useVerComoActivo } from '../../lib/ver-como';

const RUTAS_OPERACION = [
  /^\/coordinador\/?$/,
  '/coordinador/programacion-lideres',
  '/coordinador/programacion-supervisores',
  '/coordinador/programacion-produccion',
];
const RUTAS_FACTURACION = ['/coordinador/facturacion'];
const RUTAS_ANALISIS = [
  '/coordinador/cumplimiento',
  '/coordinador/analisis-paros',
  '/coordinador/analisis-lineas',
  '/coordinador/analisis-personal',
  '/coordinador/resumen-semanal',
  '/coordinador/informe',
  '/coordinador/linea',
  '/coordinador/personal',
];
const RUTAS_CONFIGURACION = ['/coordinador/configuracion'];
const RUTAS_PLANTA_CONECTADA = [
  '/coordinador/integracion',
  '/coordinador/sobrellenado',
  '/coordinador/cambios-referencia',
];

export function CoordinadorSidebar() {
  const { state } = useAuth();
  const me = useMe();
  const verComoActivo = useVerComoActivo();
  // El directivo entra a estas pantallas en su propio drill (nav podado). PERO
  // en modo "Ver como" está suplantando a un coordinador real → nav COMPLETO,
  // idéntico al de esa persona.
  const esDirectivo =
    state.kind === 'signed_in' && state.claims.rol === 'directivo' && !verComoActivo;
  // Capability del módulo Mantenimiento (fusión CMMS) — solo muestra/oculta
  // el nav; el gate duro es el API.
  const puedeVerMantenimiento = me.data?.ctx.puede_ver_mantenimiento ?? false;
  // Integración con máquinas: el grupo solo aparece si el alcance tiene líneas
  // híbridas o de máquina (sin ellas el sidebar queda idéntico). El conteo
  // de "sin señal" sale de la misma consulta.
  const conectividad = useConectividadLineas();
  const lineasConectadas = conectividad.data ?? [];
  const sinSenal = lineasConectadas.filter((l) => l.conectividad === 'sin_senal').length;
  return (
    <Sidebar>
      <SidebarGroup
        label="Operación"
        collapsible
        defaultOpen={esDirectivo}
        count={esDirectivo ? 1 : 4}
        rutas={RUTAS_OPERACION}
        storageKey="sidebar:coord:operacion"
      >
        <SidebarItem to="/coordinador" end icon="home">
          Dashboard
        </SidebarItem>
        {esDirectivo ? null : (
          <>
            <SidebarItem to="/coordinador/programacion-lideres" icon="users">
              Programar líderes
            </SidebarItem>
            <SidebarItem to="/coordinador/programacion-supervisores" icon="user-cog">
              Programar supervisores
            </SidebarItem>
            <SidebarItem to="/coordinador/programacion-produccion" icon="sitemap">
              Programar producción
            </SidebarItem>
          </>
        )}
      </SidebarGroup>
      {esDirectivo ? null : (
        <SidebarGroup
          label="Facturación"
          collapsible
          defaultOpen={false}
          count={4}
          rutas={RUTAS_FACTURACION}
          storageKey="sidebar:coord:facturacion"
        >
          <SidebarItem to="/coordinador/facturacion/programacion-op" icon="upload">
            Programación por OP
          </SidebarItem>
          <SidebarItem to="/coordinador/facturacion/cruce" icon="refresh">
            Cruce / Conciliación
          </SidebarItem>
          <SidebarItem to="/coordinador/facturacion/nivel-servicio" icon="gauge">
            Nivel de servicio
          </SidebarItem>
          <SidebarItem to="/coordinador/facturacion/q" icon="check">
            Facturación por Q
          </SidebarItem>
        </SidebarGroup>
      )}
      <SidebarGroup
        label="Análisis"
        collapsible
        defaultOpen
        count={esDirectivo ? 4 : 5}
        rutas={RUTAS_ANALISIS}
        storageKey="sidebar:coord:analisis"
      >
        <SidebarItem to="/coordinador/cumplimiento" icon="chart-bar">
          Cumplimiento
        </SidebarItem>
        <SidebarItem to="/coordinador/analisis-paros" icon="alert">
          Análisis de paros
        </SidebarItem>
        <SidebarItem to="/coordinador/analisis-personal" icon="user">
          Personal
        </SidebarItem>
        <SidebarItem to="/coordinador/resumen-semanal" icon="calendar">
          Resumen semanal
        </SidebarItem>
        {esDirectivo ? null : (
          <SidebarItem to="/coordinador/informe" icon="download">
            Informe gerencial
          </SidebarItem>
        )}
      </SidebarGroup>
      {lineasConectadas.length > 0 ? (
        <SidebarGroup
          label="Planta conectada"
          collapsible
          defaultOpen={false}
          count={3}
          rutas={RUTAS_PLANTA_CONECTADA}
          storageKey="sidebar:coord:planta-conectada"
        >
          <SidebarItem
            to="/coordinador/integracion"
            icon="wifi"
            badge={sinSenal > 0 ? String(sinSenal) : undefined}
            badgeTone="crit"
          >
            Integración de planta
          </SidebarItem>
          <SidebarItem to="/coordinador/sobrellenado" icon="scale">
            Sobrellenado
          </SidebarItem>
          <SidebarItem to="/coordinador/cambios-referencia" icon="arrows-exchange">
            Cambios de referencia
          </SidebarItem>
        </SidebarGroup>
      ) : null}
      {/* Mantenimiento oculto para el coordinador de operación hasta que su
          visual esté completa (decisión del responsable 2026-07-06); el
          directivo sí lo conserva. El gate duro sigue siendo el API. */}
      {esDirectivo && puedeVerMantenimiento ? (
        <SidebarGroup label="Mantenimiento">
          <SidebarItem to="/mantenimiento" icon="wrench">
            Mantenimiento
          </SidebarItem>
        </SidebarGroup>
      ) : null}
      {esDirectivo ? null : (
        <>
          <SidebarGroup
            label="Configuración"
            collapsible
            defaultOpen={false}
            count={6}
            rutas={RUTAS_CONFIGURACION}
            storageKey="sidebar:coord:configuracion"
          >
            <SidebarItem to="/coordinador/configuracion/skus" icon="factory">
              SKUs y capacidades
            </SidebarItem>
            <SidebarItem to="/coordinador/configuracion/tiendas" icon="storefront">
              Tiendas
            </SidebarItem>
            <SidebarItem to="/coordinador/configuracion/turnos" icon="clock">
              Turnos
            </SidebarItem>
            <SidebarItem to="/coordinador/configuracion/metas-pits" icon="settings">
              Metas Entrada a Pits
            </SidebarItem>
            <SidebarItem to="/coordinador/configuracion/usuarios" icon="shield">
              Usuarios
            </SidebarItem>
            <SidebarItem to="/coordinador/configuracion/pantalla" icon="eye">
              Pantalla de planta
            </SidebarItem>
          </SidebarGroup>
          {/* Consulta, no configuración: suelta y siempre visible. */}
          <SidebarGroup className="mt-2 border-t border-border pt-3">
            <SidebarItem to="/coordinador/auditoria" icon="search">
              Auditoría
            </SidebarItem>
          </SidebarGroup>
        </>
      )}
    </Sidebar>
  );
}
